import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';
import { ColorUtil } from '../../../utils/color-util';
import { backgroundColorNames } from '../../../theme/axiom-theme';

export const AttentionColors = Object.values(backgroundColorNames);

export type AttentionColorType = (typeof AttentionColors)[number];

export type AttentionProps = {
  /**
   * Whatever is being announced. The ring is drawn around it and never over it, so the child keeps
   * its own surface, its own handlers and its own focus.
   */
  children: React.ReactNode;
  /**
   * Which color the ring pulses in, from the same palette `Badge` draws its `background` from.
   */
  color?: AttentionColorType;
  /** Which color `highlighted` washes the child in, from that same palette. */
  highlightBackground?: AttentionColorType;
  /**
   * Washes the child in `highlightBackground`, to say it is worth looking at. **Longer-lived than
   * the ring**: the ring announces that something arrived, this holds until the reader has dealt
   * with it - so the two are driven separately and often overlap.
   *
   * **It clears itself the moment the reader touches or focuses the child**, which is what dealing
   * with it means, and lights again the next time this goes from `false` to `true`. So a caller
   * that cannot see the interaction still gets the light going out at the right moment, and one
   * that can - by clearing this itself - agrees with it rather than fighting it.
   */
  highlighted?: boolean;
  name?: string;
  /**
   * Announces, and says which announcement this is. Truthy runs the ring; **any change to the value
   * runs it again**, which is the only way to replay a CSS animation - so a second arrival while the
   * first is still travelling gets its own run. A counter is the usual thing to hand it.
   */
  trigger?: boolean | number | string;
};

/**
 * A ring that pulses outwards from whatever it is given, to say something arrived there.
 *
 * It wraps rather than paints: the child is untouched, and the ring is a sibling that takes the
 * child's box and its corners. So the same component announces a button, a group of them, a card or
 * a count, and none of them has to know it is being announced.
 *
 * `highlighted` is the held announcement to the ring's fleeting one: a wash of color laid over the
 * child rather than a repaint of it, so it lights anything without that thing knowing how - and it
 * goes out by itself the moment the reader reaches the child.
 *
 * Both read their corner radius off the child, so they follow a rounded control round rather than
 * boxing it. What cannot be read - a child that renders no element of its own, or one whose radius
 * does not resolve - is drawn as a circle rather than as a guess at a shape.
 */
export const Attention = ({
  children,
  color = backgroundColorNames.green,
  highlightBackground = backgroundColorNames.green,
  highlighted = false,
  name,
  trigger = false,
}: AttentionProps) => {
  const wrapper = useRef<HTMLSpanElement>(null);
  const overlay = useRef<HTMLSpanElement>(null);
  const [radius, setRadius] = useState<string>();
  const [dealtWith, setDealtWith] = useState(false);

  /*
    Runs on the `highlighted` transition so a fresh lighting is not silenced by an earlier dismissal.
    Resetting during render instead silently drops the reset.
  */
  useEffect(() => {
    if (highlighted) setDealtWith(false);
  }, [highlighted]);

  /*
    No dependency list on purpose: the child can change its radius without this component seeing
    it, and setting the same string bails out. A first element child that is the overlay means no
    child element rendered, so it is left unset rather than measuring the layer against itself.
  */
  useLayoutEffect(() => {
    const announced = wrapper.current?.firstElementChild;

    setRadius(
      announced && announced !== overlay.current
        ? window.getComputedStyle(announced).borderRadius || undefined
        : undefined
    );
  });

  return (
    <span
      className={AttrsHelper.formatClassname(
        'attention',
        ColorUtil.nameClass({ color, prefix: 'attention-color' }),
        ColorUtil.nameClass({
          color: highlightBackground,
          prefix: 'attention-highlight',
        }),
        highlighted && !dealtWith && 'attention-highlighted',
        trigger && 'attention-pulsing'
      )}
      data-test={name}
      /*
        Caught on the wrapper because the child is caller-owned and has its own handlers; both
        events bubble out of it. Focus is included so keyboard arrival clears the light too.
      */
      onFocus={() => {
        setDealtWith(true);
      }}
      onPointerDown={() => {
        setDealtWith(true);
      }}
      ref={wrapper}
    >
      {children}
      {/* Always mounted so lighting up mounts nothing; the ring comes second to draw over the wash. */}
      <span
        className="attention-overlay"
        data-test="ATTENTION_OVERLAY"
        ref={overlay}
        style={{ borderRadius: radius }}
      />
      {/* Keyed by the trigger: a fresh element is what restarts the animation. */}
      <span
        key={String(trigger)}
        className="attention-ring"
        data-test="ATTENTION_RING"
        style={{ borderRadius: radius }}
      />
    </span>
  );
};
